import i18n from "i18next";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCanEdit } from "@/lib/documentAccess";
import { IconCloudOff } from "@tabler/icons-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/lilia-ui/popover";
import { Button } from "@/components/lilia-ui/button";
import { useOpenDocumentStore } from "@/store/openDocumentStore";
import { testIds } from "@/testIds";
import { cn } from "@/lib/cn";
import { useSaveMoment, type SaveDisplay } from "./useSaveMoment";
import { SaveIndicatorCard } from "./SaveIndicatorCard";
import { FunArt } from "./FunArt";
import "./saveIndicator.css";

/**
 * SaveIndicator — the saved moment (Olivia, 27 Sep 2026).
 *
 * The moment happens where you'll look for it again: the indicator beside
 * the document title IS the moment. It blooms green, says so in words, and
 * settles back — no corner toast, and nothing in the canvas moves. Autosave
 * is continuous, so the full moment is rare and for a reason (see
 * lib/savedMoment/momentRules); the rest of the time it is a tick you only
 * notice if you look.
 *
 * Replaces DocumentSaveState. Its contract carries over: the pill is still
 * `doc-save-state` with the same `data-state` values, plus `version`. The
 * new root, `save-indicator`, is the group around it (the pill, Retry, the
 * Fun art and the live regions) and adds `data-moment`.
 *
 * Design: lilia-docs/design-handoffs/2026-09-27-saved-moment/
 */

interface StateCopy {
  label: string;
  /** Loud states keep a phrase at every width; this is it when space is short. */
  short?: string;
  /** Loud states never lose their word; calm ones drop it below 1200 px. */
  loud?: boolean;
  aria: string;
}

// Getters: each read is in the interface's language.
const t = (key: string) => i18n.t(`editor:saveState.${key}`);
const COPY: Record<SaveDisplay, StateCopy> = {
  saved: { get label() { return t("saved"); }, get aria() { return t("saved"); } },
  saving: { get label() { return t("saving"); }, get aria() { return t("savingAria"); } },
  offline: { get label() { return t("offline"); }, get short() { return t("offlineShort"); }, loud: true, get aria() { return t("offlineAria"); } },
  failed: { get label() { return t("failed"); }, loud: true, get aria() { return t("failed"); } },
  version: { get label() { return t("version"); }, loud: true, get aria() { return t("version"); } },
  new: { get label() { return t("new"); }, get aria() { return t("newAria"); } },
};

/** How long the pointer rests on the indicator before the card opens. */
const HOVER_OPEN_MS = 350;
const HOVER_CLOSE_MS = 220;

/* Glyphs are drawn, not typed: a ✓ character would be text inside the pill,
   and the pill's text is what "drops its word" at narrow widths promises. */
const TICK = <path d="M5.2 9.4 7.9 12 12.9 6.6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />;
const BANG = (
  <>
    <rect x="8" y="4" width="2.2" height="7" rx="1.1" fill="currentColor" />
    <circle cx="9.1" cy="13.4" r="1.3" fill="currentColor" />
  </>
);
const DIAMOND = <path d="M9 4.6 13.4 9 9 13.4 4.6 9Z" fill="currentColor" />;

function Disc({ kind, children }: { kind: "saved" | "failed" | "version" | "new"; children?: React.ReactNode }) {
  return (
    <span className={`si-disc si-disc--${kind}`} aria-hidden>
      {children && (
        <svg viewBox="0 0 18 18" width="18" height="18" focusable="false">
          {children}
        </svg>
      )}
    </span>
  );
}

function Glyph({ display }: { display: SaveDisplay }) {
  switch (display) {
    case "saving":
      return <span className="si-spinner" aria-hidden />;
    case "offline":
      return <IconCloudOff size={17} stroke={2} className="si-offline-glyph" aria-hidden />;
    case "failed":
      return <Disc kind="failed">{BANG}</Disc>;
    case "version":
      return <Disc kind="version">{DIAMOND}</Disc>;
    case "new":
      return <Disc kind="new" />;
    default:
      return <Disc kind="saved">{TICK}</Disc>;
  }
}

/** The resting words. Calm states drop theirs below 1200 px; loud ones keep a
 *  phrase at every width and only ever shorten it. */
function Words({ display, versionName }: { display: SaveDisplay; versionName: string | null }) {
  const copy = COPY[display];
  if (!copy.loud) return <span className="si-label si-label--calm">{copy.label}</span>;
  return (
    <span className="si-label si-label--loud">
      {copy.short ? (
        <>
          <span className="si-wide">{copy.label}</span>
          <span className="si-narrow">{copy.short}</span>
        </>
      ) : (
        copy.label
      )}
      {display === "version" && versionName && (
        <>
          {" · "}
          <span className="si-version-name">“{versionName}”</span>
        </>
      )}
    </span>
  );
}

/**
 * Whether the pill, grown by the words, still ends before the next control in
 * the bar. The bar's children after the one holding the pill are the
 * document's controls and the app's own; empty spacers do not count.
 */
function longWordsFit(pill: HTMLElement | null, words: HTMLElement | null): boolean {
  if (!pill || !words) return false;
  const bar = pill.closest("header");
  const pillBox = pill.getBoundingClientRect();
  let limit = bar ? bar.getBoundingClientRect().right : window.innerWidth;
  if (bar) {
    for (const child of Array.from(bar.children)) {
      if (child.contains(pill)) continue;
      const box = child.getBoundingClientRect();
      if (box.width === 0 || box.left < pillBox.right - 1) continue;
      if (!child.textContent?.trim() && child.childElementCount === 0) continue; // a spacer
      limit = Math.min(limit, box.left);
    }
  }
  return pillBox.right + words.getBoundingClientRect().width + 12 <= limit;
}

/**
 * The save moment beside the title, for someone who can change the document. A
 * reader who can't has nothing to save, so nothing shows (Olivia, 10 Oct).
 */
export function SaveIndicator() {
  return useCanEdit() ? <SaveIndicatorShown /> : null;
}

function SaveIndicatorShown() {
  const m = useSaveMoment();
  const canEdit = useOpenDocumentStore((s) => s.onRename !== null);
  const copy = COPY[m.display];

  // ── The card: hover or click, a real popover ─────────────────────────
  const [open, setOpen] = useState(false);
  const openedBy = useRef<"hover" | "click" | null>(null);
  const closedByHover = useRef(false);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const clearTimers = () => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    openTimer.current = closeTimer.current = null;
  };
  useEffect(() => clearTimers, []);

  const hoverIn = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    if (open || openTimer.current) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      openedBy.current = "hover";
      setOpen(true);
    }, HOVER_OPEN_MS);
  }, [open]);

  const hoverOut = useCallback(() => {
    if (openTimer.current) window.clearTimeout(openTimer.current);
    openTimer.current = null;
    if (!open || openedBy.current !== "hover") return;
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      closedByHover.current = true;
      setOpen(false);
    }, HOVER_CLOSE_MS);
  }, [open]);

  const onOpenChange = (next: boolean) => {
    clearTimers();
    if (!next && openedBy.current === "hover" && open) {
      // A click on a card the pointer opened keeps it, and makes it the
      // clicked kind: focus moves in, and Esc brings it back.
      openedBy.current = "click";
      setOpen(true);
      return;
    }
    if (next) openedBy.current = "click";
    setOpen(next);
  };

  const closeCard = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, []);

  const playing = m.playing;
  const playKind = playing?.kind;

  // "every change is safe" grows into the bar's free space — and only if it
  // fits there: the bloom must never slide over Export or the document's
  // other controls. Measured when each full moment starts, before paint.
  const pillRef = useRef<HTMLButtonElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [wordsFit, setWordsFit] = useState(true);
  useLayoutEffect(() => {
    if (playing?.kind !== "full") return;
    setWordsFit(longWordsFit(pillRef.current, measureRef.current));
  }, [playing?.id, playing?.kind]);
  const showWords = playKind === "full" && m.display !== "version" && wordsFit;

  return (
    <span
      data-testid={testIds.editor.saveIndicator}
      data-state={m.display}
      data-moment={m.last?.kind}
      data-moment-playing={playing ? "true" : "false"}
      data-full-count={m.fullCount}
      data-tick-count={m.tickCount}
      data-fun-style={m.last?.funStyle ?? undefined}
      className="si-root"
    >
      <Popover open={open} onOpenChange={onOpenChange}>
        <span className="si-anchor" onPointerEnter={hoverIn} onPointerLeave={hoverOut}>
          {/* Sizes the slot to the RESTING indicator. The pill sits over it and
              may grow during the bloom without moving the title beside it. */}
          <span className="si-sizer si-pill-box" aria-hidden data-state={m.display}>
            <Glyph display={m.display} />
            <Words display={m.display} versionName={m.versionName} />
          </span>
          <span ref={measureRef} className="si-long-measure" aria-hidden>
            · {i18n.t("editor:saveState.everySafe")}
          </span>
          <PopoverTrigger asChild>
            <button
              ref={pillRef}
              type="button"
              aria-label={m.display === "version" && m.versionName ? `Version "${m.versionName}" saved` : copy.aria}
              aria-haspopup="dialog"
              data-testid={testIds.editor.docSaveState}
              data-state={m.display}
              className={cn("si-pill si-pill-box", !copy.loud && "si-pill--calm")}
            >
              {/* The bloom's ground, keyed so each moment replays. */}
              <span key={`b${playing?.id ?? 0}`} className="si-bloom" data-play={playKind} aria-hidden />
              <span key={`g${playing?.id ?? 0}`} className="si-glyph" data-play={playKind}>
                {(m.display === "saved" || m.display === "version") && <span className="si-ring" aria-hidden />}
                <Glyph display={m.display} />
              </span>
              <Words display={m.display} versionName={m.versionName} />
              {/* A named version's label already says it; the words are for
                  the other full moments. */}
              <span key={`l${playing?.id ?? 0}`} className="si-long" data-play={showWords ? playKind : undefined} aria-hidden>
                · {i18n.t("editor:saveState.everySafe")}
              </span>
            </button>
          </PopoverTrigger>
        </span>

        {m.display === "failed" && (
          <Button
            type="button"
            size="xs"
            variant="outline"
            data-testid={testIds.editor.saveIndicatorRetry}
            onClick={m.retry}
            className="h-[26px] px-2.5 text-[12px]"
          >
            {t("retry")}
          </Button>
        )}

        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[300px] overflow-hidden p-0"
          onPointerEnter={hoverIn}
          onPointerLeave={hoverOut}
          // Opened by the pointer, the card must not take focus from the page:
          // the author may be mid-sentence. Opened by a click, it does.
          onOpenAutoFocus={(e) => {
            if (openedBy.current === "hover") e.preventDefault();
          }}
          // Closing because the pointer left must not move focus either. Esc
          // and a click outside return it to the indicator.
          onCloseAutoFocus={(e) => {
            if (closedByHover.current) e.preventDefault();
            closedByHover.current = false;
          }}
        >
          <SaveIndicatorCard
            display={m.display}
            documentId={m.documentId}
            lastSyncedAt={m.lastSyncedAt}
            pendingCount={m.pendingCount}
            detail={m.detail}
            sessionVersion={m.lastVersion}
            canEdit={canEdit}
            onClose={closeCard}
          />
        </PopoverContent>
      </Popover>

      {playing?.kind === "full" && playing.funStyle && <FunArt style={playing.funStyle} momentId={playing.id} />}

      {/* Announcements: polite for full moments only, assertive for a failed
          save. Ticks are never announced — "saved" every three seconds would
          make a screen reader unusable. */}
      <span className="sr-only" role="status" aria-live="polite" data-testid="save-indicator-announce">
        {m.politeMessage}
      </span>
      <span className="sr-only" role="alert" aria-live="assertive">
        {m.display === "failed" ? "Couldn't save" : ""}
      </span>
    </span>
  );
}
